import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { FilterMatchMode } from 'primereact/api';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { errorService } from '../../services/ErrorService';
import { showErrorToast, showSuccessToast } from '../../utils/ToastUtils';
import { Menu } from 'primereact/menu';
import { Skeleton } from 'primereact/skeleton';

import { 
    selectLoading, 
    selectMedicalServiceList, 
    selectMedicalServiceListCount, 
    getMedicalServicesAsync, 
    getMedicalServicesCountAsync, 
    deleteMedicalServiceAsync, 
    editMedicalServiceAsync,
    createMedicalServiceAsync,
} from './medicalServiceSlice';

const MedicalServiceView = () => {

    let emptyMedicalService = {
        id: null,
        name: "",
        state: ""
    }

    const defaultGridParameters = {page: 0, first: 0, rows: 10, sortField: 'id', sortOrder: 1, filters: {}}
    
    const [deleteMedicalServiceDialog, setDeleteMedicalServiceDialog] = useState(false);
    
    const [medicalServiceDialog, setMedicalServiceDialog] = useState(false);
    const [medicalService, setMedicalService] = useState(emptyMedicalService);
    const [selectedMedicalServices, setSelectedMedicalServices] = useState(null);

    const [gridParameters, setGridParameters] = useState(defaultGridParameters);

    const toast = useRef(null);
    const dt = useRef(null);
    const menu = useRef(null);
    const [detail, setDetail] = useState({});

    const dispatch = useDispatch();
    const loading = useSelector(selectLoading);
    const medicalServiceList = useSelector(selectMedicalServiceList);
    const medicalServiceListCount = useSelector(selectMedicalServiceListCount);

    useEffect(() => {
        const loadInitialData = async () => {
            try {
                await errorService.retryOperation(async () => {
                    await Promise.all([
                        dispatch(getMedicalServicesAsync(defaultGridParameters)).unwrap(),
                        dispatch(getMedicalServicesCountAsync(defaultGridParameters)).unwrap()
                    ]);
                });
            } catch (error) {
                showErrorToast(toast, 'Erro ao carregar serviços médicos', error);
            }
        };

        loadInitialData();
    }, [dispatch]);

    const loadData = async () => {
        try {
            await errorService.retryOperation(async () => {
                await Promise.all([
                    dispatch(getMedicalServicesAsync(gridParameters)).unwrap(),
                    dispatch(getMedicalServicesCountAsync(gridParameters)).unwrap()
                ]);
            });
        } catch (error) {
            showErrorToast(toast, 'Erro ao carregar dados', error);
        }
    };

    const initialLoadDataGrid = async () => {
        loadData();
    };

    const loadDataGrid = async () => {
        loadData();
    };

    const onSort = async (event) => {
        try {
            await errorService.retryOperation(async () => {
                const sortParams = {
                    ...gridParameters,
                    sortField: event.sortField,
                    sortOrder: event.sortOrder,
                    filters: {...gridParameters.filters}
                };
                setGridParameters(sortParams);
                await Promise.all([
                    dispatch(getMedicalServicesAsync(sortParams)).unwrap(),
                    dispatch(getMedicalServicesCountAsync(sortParams)).unwrap()
                ]);
            });
        } catch (error) {
            showErrorToast(toast, 'Erro ao ordenar serviços médicos', error);
        }
    };

    const onFilter = async (event) => {
        try {
            await errorService.retryOperation(async () => {
                const filterParams = {
                    ...gridParameters,
                    page: 0,
                    first: 0,
                    filters: event.filters
                };
                setGridParameters(filterParams);
                await Promise.all([
                    dispatch(getMedicalServicesAsync(filterParams)).unwrap(),
                    dispatch(getMedicalServicesCountAsync(filterParams)).unwrap()
                ]);
            });
        } catch (error) {
            showErrorToast(toast, 'Erro ao filtrar serviços médicos', error);
        }
    };

    const onPage = async (event) => {
        try {
            await errorService.retryOperation(async () => {
                const pageParams = {
                    ...event,
                    filters: {...gridParameters.filters}
                };
                setGridParameters(pageParams);
                await dispatch(getMedicalServicesAsync(pageParams)).unwrap();
            });
        } catch (error) {
            showErrorToast(toast, 'Erro ao mudar página', error);
        }
    };

    const openNew = () => {
        setMedicalService(emptyMedicalService);
        setMedicalServiceDialog(true);
    }

    const hideMedicalServiceDialog = () => {
        setMedicalServiceDialog(false);
    }

    const hideDeleteMedicalServiceDialog = () => {
        setDeleteMedicalServiceDialog(false);
    }
    
    const validateForm = () => {
        if (!medicalService.name) {
            showErrorToast(toast, 'Erro de validação', 'O nome do serviço médico é obrigatório');
            return false;
        }

        if (!medicalService.state) {
            showErrorToast(toast, 'Erro de validação', 'A UF do serviço médico é obrigatória');
            return false;
        }

        return true;
    }

    const onSave = async () => {
        if (validateForm()) {
            try {
                await errorService.retryOperation(async () => {
                    if (medicalService.id) {
                        await dispatch(editMedicalServiceAsync({medicalService, gridParameters})).unwrap();
                        showSuccessToast(toast, 'Sucesso', 'Serviço médico atualizado com sucesso');
                    } else {
                        await dispatch(createMedicalServiceAsync({medicalService, gridParameters})).unwrap();
                        showSuccessToast(toast, 'Sucesso', 'Serviço médico criado com sucesso');
                    }
                    hideMedicalServiceDialog();
                    await loadData();
                });
            } catch (error) {
                showErrorToast(toast, 'Erro ao salvar serviço médico', error);
            }
        }
    };

    const editMedicalService = (medicalService) => {
        setMedicalService({ ...medicalService });
        setMedicalServiceDialog(true);
    }

    const confirmDeleteMedicalService = (medicalService) => {
        setMedicalService(medicalService);
        setDeleteMedicalServiceDialog(true);
    }

    const deleteMedicalService = async () => {
        try {
            await errorService.retryOperation(async () => {
                await dispatch(deleteMedicalServiceAsync({id: medicalService.id, gridParameters})).unwrap();
                showSuccessToast(toast, 'Sucesso', 'Serviço médico excluído com sucesso');
                hideDeleteMedicalServiceDialog();
                setMedicalService(emptyMedicalService);
                setGridParameters({...gridParameters, offset: 0, first: 0});
                await loadData();
            });
        } catch (error) {
            showErrorToast(toast, 'Erro ao excluir serviço médico', error);
        }
    };

    const exportCSV = () => {
        dt.current.exportCSV();
    }

    const confirmDeleteSelected = () => {
        // setDeleteMedicalServicesDialog(true);
    }

    const onInputChangeMedicalService = (e, name) => {
        const val = (e.target && e.target.value) || '';
        let _medicalService = { ...medicalService };
        _medicalService[`${name}`] = val;
        setMedicalService(_medicalService);
    }

    const leftToolbarTemplate = () => {
        return (
            <React.Fragment>
                <div className="my-2">
                    <Button label="Novo serviço" icon="pi pi-plus" className="p-button-success mr-2" onClick={openNew} />
                    {/* <Button label="Delete" icon="pi pi-trash" className="p-button-danger" onClick={confirmDeleteSelected} disabled={!selectedProducts || !selectedProducts.length} /> */}
                </div>
            </React.Fragment>
        )
    }

    const toggleMenu = (event, data) => {
        setDetail(data)
        menu.current.toggle(event);
    };

    const toolbarItems = [
        {
            label: 'Editar',
            icon: 'pi pi-pencil',
            command: () => editMedicalService(detail)
        },
        {
            label: 'Excluir',
            icon: 'pi pi-trash',
            command: () => confirmDeleteMedicalService(detail)
        }
    ];

    const actionBodyTemplate = (rowData) => {
        return <>
            <Menu ref={menu} model={toolbarItems} popup />
            <Button type="button" label="Ações" icon="pi pi-angle-down" onClick={(event) => toggleMenu(event, rowData)} style={{ width: 'auto' }} />
        </>;
    }

    const idBodyTemplate = (rowData) => {
        return (
            <>
                <span className="p-column-title">ID</span>
                {rowData.id}
            </>
        );
    }

    const ufBodyTemplate = (rowData) => {
        return (
            <>
                <span className="p-column-title">UF</span>
                {rowData.state}
            </>
        );
    }

    const nameBodyTemplate = (rowData) => {
        return (
            <>
                <span className="p-column-title">Nome</span>
                {rowData.name}
            </>
        );
    }

    const medicalServiceDialogFooter = (
        <>
            <Button label="Cancelar" icon="pi pi-times" className="p-button-text" onClick={hideMedicalServiceDialog} />
            <Button label="Salvar" icon="pi pi-check" className="p-button-text" onClick={onSave} />
        </>
    );

    const deleteMedicalServiceDialogFooter = (
        <>
            <Button label="Não" icon="pi pi-times" className="p-button-text" onClick={hideDeleteMedicalServiceDialog} />
            <Button label="Sim" icon="pi pi-check" className="p-button-text" onClick={deleteMedicalService} />
        </>
    );

    return (
      <>
        <div className="grid crud-demo">
            <div className="col-12">
                <div className="card">
                    <Toast ref={toast} />
                    <div className="flex justify-content-between align-items-center mb-3">
                        <h5 className="m-0">Gestão de serviços</h5>
                        <Button
                            icon="pi pi-refresh"
                            rounded
                            text
                            onClick={loadData}
                            disabled={loading}
                        />
                    </div>
                    <DataTable ref={dt} value={loading ? Array.from({ length: gridParameters.rows }, (_, i) => ({ id: `skeleton-${i}` })) : medicalServiceList} selection={selectedMedicalServices} onSelectionChange={(e) => setSelectedMedicalServices(e.value)}
                        header={leftToolbarTemplate()}
                        dataKey="id"
                        paginator
                        sortField={gridParameters.sortField}
                        sortOrder={gridParameters.sortOrder}
                        rows={gridParameters.rows}
                        rowsPerPageOptions={[5, 10, 25, 50, 100]}
                        className="datatable-responsive"
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                        currentPageReportTemplate="Exibindo de {first} à {last} de {totalRecords} registros"
                        emptyMessage="Sem resultados."
                        responsiveLayout="scroll"
                        totalRecords={medicalServiceListCount}
                        lazy={true}
                        first={gridParameters.first}
                        onSort={(e) => onSort(e)}
                        onFilter={(e) => onFilter(e)}
                        onPage={(e) => onPage(e)}
                        >
                        <Column field="id" header="ID" sortable={!loading} body={loading ? () => <Skeleton /> : idBodyTemplate} headerStyle={{ width: '1em', minWidth: '5rem' }}></Column>
                        <Column field="name" header="Nome" sortable={!loading} body={loading ? () => <Skeleton /> : nameBodyTemplate} headerStyle={{ width: '1em', minWidth: '10rem' }}></Column>
                        <Column field="state" header="UF"  filterPlaceholder="Buscar por UF" showAddButton={false} showFilterOperator={false} filterMatchMode={FilterMatchMode.EQUALS} filterMatchModeOptions={[FilterMatchMode.EQUALS]} sortable={!loading} body={loading ? () => <Skeleton /> : ufBodyTemplate} headerStyle={{ width: '1em', minWidth: '10rem' }}></Column>
                        <Column body={loading ? () => <Skeleton /> : actionBodyTemplate} style={{ width: '15%', minWidth: '10rem' }}></Column>
                    </DataTable>

                    {/* Editar Serviço */}
                    <Dialog visible={medicalServiceDialog} style={{ width: '450px' }} header="Editar serviço" modal className="p-fluid" footer={medicalServiceDialogFooter} onHide={hideMedicalServiceDialog}>
                        <h5>{medicalService.title}</h5>

                        <div className="field">
                            <label htmlFor="name">Nome</label>
                            <InputText id="name" value={medicalService.name} onChange={(e) => onInputChangeMedicalService(e, 'name')} required />
                        </div>

                        <div className="field">
                            <label htmlFor="state">UF</label>
                            <InputText id="state" value={medicalService.state} onChange={(e) => onInputChangeMedicalService(e, 'state')} required />
                        </div>
                    </Dialog>
                    {/* FIM - Editar Serviço */}

                    <Dialog visible={deleteMedicalServiceDialog} style={{ width: '450px' }} header="Confirmar exclusão" modal footer={deleteMedicalServiceDialogFooter} onHide={hideDeleteMedicalServiceDialog}>
                        <div className="flex align-items-center justify-content-center">
                            <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
                            {medicalService && <span>Você deseja realmente excluir: <b>{medicalService.name}</b>?</span>}
                        </div>
                    </Dialog>
                </div>
            </div>
        </div>
      </>
    );
}

const comparisonFn = function (prevProps, nextProps) {
    return prevProps.location.pathname === nextProps.location.pathname;
};

export default React.memo(MedicalServiceView, comparisonFn);